import React from 'react';
import { useAppSelector, useAppDispatch } from '@/hooks/useAppDispatch';
import { toggleStorageSidebar } from '@/store/slices/storageSlice';
import { useDroppable } from '@dnd-kit/core';
import { cn } from '@/lib/utils';
import { ChevronRight, ChevronLeft, Package, Warehouse, Truck, Home, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';

interface StorageLocationProps {
  id: string;
  name: string;
  warehouse: string;
  capacity: number;
  currentVolume: number;
}

const StorageLocation: React.FC<StorageLocationProps> = ({ id, name, warehouse, currentVolume }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `storage-${id}`,
    data: {
      type: 'storage',
      locationId: id,
      name
    }
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex flex-col gap-1 p-3 rounded-lg border transition-colors',
        isOver ? 'bg-primary/10 border-primary' : 'bg-card border-transparent hover:bg-accent/50'
      )}>
      <div className="flex justify-between items-start">
        <div>
          <h4 className="font-semibold text-sm">{name}</h4>
          <p className="text-xs text-muted-foreground">{warehouse}</p>
        </div>
        <Badge variant="secondary" className="text-xs h-6">
          {currentVolume} m³
        </Badge>
      </div>
    </div>
  );
};

export const StorageSidebar: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isSidebarOpen, locations } = useAppSelector((state) => state.storage);

  if (!isSidebarOpen) {
    return (
      <div className="h-full border-l border-border bg-background flex flex-col items-center py-4 w-12 shrink-0">
        <Button variant="ghost" size="icon" onClick={() => dispatch(toggleStorageSidebar())} className="mb-4">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <div className="flex flex-col gap-4">
          <Button variant="ghost" size="icon">
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon">
            <Package className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full border-l border-border bg-background flex flex-col w-80 shrink-0 transition-all duration-300">
      <div className="p-4 border-b border-border flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Package className="w-4 h-4" />
          <h3 className="font-semibold">Storage</h3>
        </div>
        <Button variant="ghost" size="icon" onClick={() => dispatch(toggleStorageSidebar())} className="h-8 w-8">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Quick Receive Section */}
          <div>
            <div className="flex items-center gap-2 mb-3 text-muted-foreground">
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Quick Receive</span>
            </div>
          </div>

          {/* Storage Locations */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Warehouse className="w-4 h-4" />
                <span className="text-sm font-medium">Storage</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground rotate-90" />
            </div>
            <div className="space-y-2">
              {locations.map((location) => (
                <StorageLocation key={location.id} {...location} />
              ))}
            </div>
          </div>

          {/* Transshipment Point */}
          <div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Truck className="w-4 h-4" />
                <span className="text-sm font-medium">Transshipment point</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>

          {/* Customer Address */}
          <div>
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Home className="w-4 h-4" />
                <span className="text-sm font-medium">Customer Address</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};
